import React from 'react';
import styled from 'styled-components';


const TermsEn: React.FC = () => {

  return (
    <>
      <Section>
        <Title>Terms of service</Title>
        <Text>
          These Terms of Service (“Terms”) explain how you may use Architrave (the “Service”) and the rules that apply between you and Architrave (the “Company”).
        </Text>
      </Section>

      <Section>
        <SmallTitle>Purpose</SmallTitle>
        <Text>
          These Terms describe your rights and obligations when using our SaaS portfolio platform.
        </Text>
      </Section>

      <Section>
        <SmallTitle>Definitions</SmallTitle>
        <Text>
          <List2>
            <li><Strong>Service</Strong>: A web-based platform for uploading artwork and related information to create and manage a personal portfolio site.</li>
            <li><Strong>Member</Strong>: An individual who signs up for the Service and provides required information.</li>
            <li><Strong>Paid member</Strong>: A member who subscribes to premium features via recurring billing.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Posting and changes to these terms</SmallTitle>
        <Text>
          <List2>
            <li>We post these Terms on the Service’s main page or a linked page so you can easily review them.</li>
            <li>We may amend these Terms as permitted by law and we state the changes and the effective date; we give at least 7 days’ prior notice (or 30 days if a change is unfavorable to you) via in-Service posting and the contact information you provided (e.g., email).</li>
            <li>We deem you to have accepted the amended Terms if you continue using the Service without objection before the effective date; if you object, either you or we may terminate the agreement, and no party owes damages due to that termination.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>What the service includes</SmallTitle>
        <Text>
          <List2>
            <li>
              Core features:
              <List>
                <li>Portfolio site creation and management</li>
                <li>Artwork and profile uploads</li>
                <li>File downloads (e.g., CV, images)</li>
              </List>
            </li>
            <li>We may evolve or refine features over time.</li>
            <li>We may change or discontinue parts or all of the Service with reasonable prior notice.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Service interruptions</SmallTitle>
        <Text>
          <List2>
            <li>We may conduct up to two scheduled maintenance windows per month, each up to 1 hour. The standing notice posted within the Service counts as prior notice, and a window may pass without any interruption.</li>
            <li>We may temporarily suspend the Service for maintenance or to address security vulnerabilities or incidents requiring immediate action; where feasible, we will provide notice in advance or afterward.</li>
            <li>Unforeseeable events (force majeure)—such as natural disasters or large-scale system failures—may prevent prior notice.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Sign-up</SmallTitle>
        <Text>
          <List2>
            <li>You must be at least 14 years old and confirm this during sign-up.</li>
            <li>Email verification is required; social logins are not supported.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Account termination and restrictions</SmallTitle>
        <Text>
          <List2>
            <li>You may delete your account at any time. We delete data in line with our Privacy Policy and applicable law (some records may be retained where required).</li>
            {/* UPDATED: clear grounds + prior notice where feasible */}
            <li>
              We may restrict or terminate accounts—<Strong>with prior notice where feasible</Strong>—for:
              <List>
                <li>impersonation or use of others’ information,</li>
                <li>illegal or offensive content,</li>
                <li>interference with the Service or <Strong>abnormal use</Strong>,</li>
                <li><Strong>attempted security breaches</Strong>,</li>
                <li><Strong>abuse of trials</Strong> or creating multiple accounts for repetitive use.</li>
              </List>
            </li>
          </List2>
        </Text>
      </Section>

      {/* REMOVED: earlier duplicate "User notices" section; consolidated below */}

      <Section>
        <SmallTitle>Starting a paid subscription</SmallTitle>
        <Text>
          <List2>
            <li>Your paid subscription starts once sign-up and payment are complete.</li>
            <li>Payments are processed via Polar.sh (Stripe-based) and support monthly recurring billing only.</li>
            <li>Access is granted immediately upon successful payment—no extra approval step.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Payments and fees</SmallTitle>
        <Text>
          <List2>
            <li>We accept Stripe-based Polar.sh payments only.</li>
            <li>Current pricing appears on the Service pricing page.</li>
            {/* NEW: no payment-method surcharge */}
            <li>We do not add a separate surcharge based on your chosen payment method.</li>
          </List2>
        </Text>
      </Section>

      {/* NEW: price change notice window */}
      <Section>
        <SmallTitle>Price changes</SmallTitle>
        <Text>
          <List2>
            <li>We may change prices.</li>
            <li>Increases or other unfavorable changes are notified at least <Strong>30 days</Strong> in advance; other changes at least <Strong>7 days</Strong> in advance.</li>
            <li>Changes take effect from your <Strong>next billing cycle</Strong>.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Cancellations and refunds</SmallTitle>
        <Text>
          <List2>
            <li>You may cancel within 7 days of purchase under Korean e-commerce rules.</li>
            <li>No cancellations if the Service has already been accessed or used, or if issues are caused by you.</li>
            <li>Refunds follow our refund policy; contact support for details.</li>
            <li>Approved refunds or billing errors are processed within <Strong>3 business days</Strong> to the original payment method.</li>
            <li>We follow Korea’s Content User Protection Guidelines.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Privacy</SmallTitle>
        <Text>
          <List2>
            <li>We handle personal data under applicable laws and our Privacy Policy, which explains data use, retention, and deletion.</li>
            <li><Strong>We do not sell your personal information.</Strong></li>
            <li>We may process data outside your country for service delivery and support, consistent with Korean law and our Privacy Policy.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Our responsibilities</SmallTitle>
        <Text>We work to provide a reliable Service and comply with law and these Terms.</Text>
      </Section>

      <Section>
        <SmallTitle>Your responsibilities</SmallTitle>
        <Text>Do not:</Text>
        <Text>
          <List>
            <li>Impersonate others</li>
            <li>Upload illegal or offensive content</li>
            <li>Disrupt or interfere with the Service</li>
            <li>Use automated tools (e.g., bots, scrapers)</li>
            <li>Reverse-engineer, copy, or commercially exploit Service content</li>
            <li>Share unauthorized materials</li>
            <li>Use the Service for unauthorized commercial purposes (e.g., resale, clone services)</li>
            <li>Share or lend your account</li>
          </List>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Content ownership and license</SmallTitle>
        <Text>
          <List2>
            <li>You retain copyright in your uploads.</li>
            <li>You are responsible for legal issues arising from your content.</li>
            <li>If you delegate site management (e.g., a sub-admin), you remain responsible for their actions.</li>
            <li>We may remove or restrict content that infringes rights or violates law, without notice.</li>
            <li>We do not have a duty to pre-screen content.</li>
            <li>Templates, designs, and UI elements belong to the Company; you may not copy, redistribute, or commercialize them.</li>
            <li>By uploading content, you grant us a <Strong>non-exclusive, royalty-free, worldwide license</Strong> to store, process (e.g., resize), transmit, and display your content to provide and protect the Service. This license ends when you delete the content, except that backups, dispute resolution, or legal compliance may require retention for a reasonable period.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Recurring billing and cancellation</SmallTitle>
        <Text>
          <List2>
            <li>If you choose recurring billing, your plan renews automatically under the same terms unless you turn it off.</li>
            <li>You can disable auto-renew anytime in settings or by contacting support.</li>
            <li>We email a receipt or confirmation after each successful charge.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Disclaimers</SmallTitle>
        <Text>
          <List2>
            <li>We are not liable for interruptions caused by force majeure.</li>
            <li>We are not responsible for issues caused by your actions.</li>
            <li>We may throttle or suspend accounts that generate abnormal traffic.</li>
            <li>We are not responsible for device, browser, or network compatibility issues.</li>
          </List2>
        </Text>
      </Section>

      {/* NEW: dedicated availability credits section */}
      <Section>
        <SmallTitle>Service availability credits</SmallTitle>
        <Text>
          <List2>
            <li>If downtime exceeds <Strong>3 consecutive hours</Strong> or <Strong>24 hours within a month</Strong> (excluding force majeure), paid users receive a <Strong>1-day extension per hour of downtime</Strong>, or equivalent credits.</li>
            <li><Strong>How to claim:</Strong> submit a request within <Strong>30 days</Strong> after the end of the affected month.</li>
            <li><Strong>Cap:</Strong> credits are limited to the subscription fee you paid for that month; <Strong>no cash refunds</Strong>.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Dispute resolution</SmallTitle>
        <Text>
          <List2>
            <li>We aim to resolve issues quickly and fairly.</li>
            <li>Contact support to submit feedback or complaints.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Governing law and jurisdiction</SmallTitle>
        <Text>
          <List2>
            <li>These Terms are governed by the laws of the Republic of Korea.</li>
            <li>Disputes are handled by the court with jurisdiction over our headquarters.</li>
            <li>For users outside Korea, the Seoul Central District Court is the court of first instance.</li>
            <li>International disputes are addressed under Korean law and these Terms.</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Precedence</SmallTitle>
        <Text>
          If these Terms conflict with separate notices, FAQs, or policies, these Terms control.
        </Text>
      </Section>

      <Section>
        <SmallTitle>Limitation of liability</SmallTitle>
        <Text>
          <List2>
            <li>Except for willful misconduct or gross negligence, our liability is limited to direct damages and capped at the fees you paid in the past 6 months (KRW 0 for free users).</li>
            <li>We are not liable for indirect, incidental, or consequential damages, or lost expected profits.</li>
            <li>This clause applies only to the extent permitted by applicable law.</li>
            {/* REMOVED: downtime credit here to avoid duplication; see "Service availability credits" */}
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Interpretation</SmallTitle>
        <Text>
          Matters not covered here are interpreted under Korean law and standard practices, including the Act on Consumer Protection in Electronic Commerce and the Act on the Regulation of Terms and Conditions.
        </Text>
      </Section>

      <Section>
        <SmallTitle>No assignment</SmallTitle>
        <Text>
          You may not assign or delegate your rights or obligations under these Terms without our prior written consent.
        </Text>
      </Section>

      <Section>
        <SmallTitle>Severability</SmallTitle>
        <Text>
          If any provision is invalid or unenforceable, the remaining provisions remain in effect.
        </Text>
      </Section>

      {/* CONSOLIDATED: user notices */}
      <Section>
        <SmallTitle>User notices</SmallTitle>
        <Text>
          <List2>
            <li>We may contact you via in-Service posting and the contact information you provided (e.g., email). General notices may be posted within the Service.</li>
            <li>You may send legal notices to: <Strong>architrave2025@gmail.com</Strong>; and by mail to: <Strong>(16911)23, Mabuk-ro 154beon-gil, Giheung-gu, Yongin-si, Gyeonggi-do, Republic of Korea</Strong> (Attn: Legal).</li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Effective dates</SmallTitle>
        <Text>
          Effective date: August 12, 2025<br />
          Last updated: August 12, 2025
        </Text>
      </Section>
    </>
  );
};

export default TermsEn;



const Title = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_01};
  ${({ theme }) => theme.typography.Body_02_1};
  margin-bottom: 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`;

const SmallTitle = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_01};
  ${({ theme }) => theme.typography.Body_03_1};
  margin-bottom: 4px;
`;

const Text = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  line-height: 1.4;
`;

const Strong = styled.strong`
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;


const List = styled.ul`
  list-style: disc;
  padding-left: 12px;
`;

const List2 = styled.ol`
  list-style: decimal;
  padding-left: 14px;
`;